import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Play, ShoppingCart, AlertCircle, X, CreditCard, Shield } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import DiscussionForum from "@/components/DiscussionForum";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function CourseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [purchased, setPurchased] = useState(false);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [paymentError, setPaymentError] = useState("");

  useEffect(() => {
    fetchData();
    loadRazorpayScript();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    try {
      const [courseRes, chaptersRes, purchaseRes] = await Promise.all([
        api.get(`/courses/${id}`),
        api.get(`/courses/${id}/chapters`),
        api.get(`/orders/check/${id}`),
      ]);
      setCourse(courseRes.data);
      setChapters(chaptersRes.data);
      setPurchased(purchaseRes.data.purchased);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleBuy = useCallback(async () => {
    if (!user) { navigate("/login"); return; }
    if (!course) return;
    setBuying(true);
    setPaymentError("");
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded || !window.Razorpay) {
        setPaymentError("Payment gateway failed to load. Check your internet connection.");
        setBuying(false);
        return;
      }
      const { data: orderData } = await api.post("/orders/create", { course_id: id });
      if (!orderData.razorpay_key || !orderData.order_id) {
        setPaymentError("Failed to create payment order. Please try again.");
        setBuying(false);
        return;
      }
      const options = {
        key: orderData.razorpay_key,
        amount: orderData.amount_paise,
        currency: orderData.currency || "INR",
        name: "Smart English Fast English",
        description: course.title,
        order_id: orderData.order_id,
        prefill: { name: user.name || "", email: user.email || "", contact: user.phone || "" },
        theme: { color: "#0284C7" },
        handler: async function (response) {
          try {
            await api.post("/orders/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setPurchased(true);
            setPaymentError("");
          } catch (err) {
            setPaymentError("Payment verification failed. If amount was deducted, contact support.");
          } finally {
            setBuying(false);
          }
        },
        modal: { ondismiss: () => setBuying(false), escape: true, backdropclose: false },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        setPaymentError(response.error?.description || "Payment failed. Please try again.");
        setBuying(false);
      });
      rzp.open();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("purchased")) {
        setPurchased(true);
      } else {
        setPaymentError(typeof detail === "string" ? detail : "Payment failed. Please try again.");
      }
      setBuying(false);
    }
  }, [user, course, id, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading...</div>;
  if (!course) return <div className="min-h-screen flex items-center justify-center text-slate-400">Course not found</div>;

  const discount = course.mrp > course.price ? Math.round(((course.mrp - course.price) / course.mrp) * 100) : 0;

  return (
    <div className="bg-white min-h-screen pb-48" data-testid="course-detail-page">
      <button data-testid="back-btn" onClick={() => navigate(-1)} className="absolute top-2 left-2 z-10 bg-black/40 text-white p-2">
        <ArrowLeft size={20} />
      </button>
      <div className="aspect-video w-full bg-slate-200 relative">
        <img src={course.image_url || "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=800&h=450&fit=crop"} alt={course.title} className="w-full h-full object-cover" />
      </div>
      <div className="px-4 pt-4">
        <span className="text-xs font-bold uppercase tracking-wider text-[#0284C7]">
          {course.category} &middot; {course.content_type === "book" ? "Book" : "Course"}
        </span>
        <h1 data-testid="course-title" className="text-xl font-black text-slate-900 mt-1 leading-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {course.title}
        </h1>
        <div className="flex items-center gap-3 mt-3">
          <span className="text-2xl font-black text-slate-900">&#8377;{course.price}</span>
          {course.mrp > course.price && (
            <>
              <span className="text-base text-slate-400 line-through">&#8377;{course.mrp}</span>
              <span className="bg-green-600 text-white text-xs font-bold px-2 py-1">{discount}% OFF</span>
            </>
          )}
        </div>
        <p className="text-sm text-slate-600 leading-relaxed mt-4">{course.description}</p>
        {chapters.length > 0 && (
          <div className="mt-6">
            <h2 className="text-base font-bold text-slate-900 mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Course Content</h2>
            <div className="space-y-1">
              {chapters.map((ch, i) => (
                <div key={ch.id} className="bg-slate-50 p-3">
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 bg-[#0284C7] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{ch.title}</p>
                      <p className="text-xs text-slate-500">{ch.videos?.length || 0} videos</p>
                    </div>
                    {purchased && <Play size={16} className="text-[#0284C7]" />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {!purchased && (
          <div className="mt-6 flex items-center gap-2 text-xs text-slate-400">
            <Shield size={14} />
            <span>Secure payment via Razorpay. UPI, Cards, Net Banking, Wallets accepted.</span>
          </div>
        )}

        {/* Discussion Forum */}
        <DiscussionForum courseId={id} />
      </div>

      {/* Sticky Buy Section */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-slate-100 z-40" data-testid="sticky-buy-section">
        {purchased ? (
          <div className="p-4">
            <button data-testid="start-course-btn" onClick={() => navigate(`/watch/${id}`)} className="w-full bg-green-600 text-white font-bold py-4 text-sm transition-none flex items-center justify-center gap-2">
              <Play size={18} /> {course.content_type === "book" ? "Read Now" : "Start Course"}
            </button>
          </div>
        ) : (
          <div className="p-4">
            {paymentError && (
              <div className="mb-3 bg-red-50 border border-red-200 p-3 flex items-start gap-2" data-testid="payment-error">
                <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-bold text-red-700">Payment Error</p>
                  <p className="text-xs text-red-600">{paymentError}</p>
                </div>
                <button onClick={() => setPaymentError("")} className="ml-auto text-red-400"><X size={14} /></button>
              </div>
            )}
            <button data-testid="buy-course-btn" onClick={handleBuy} disabled={buying} className="w-full bg-[#0284C7] text-white font-bold py-4 text-sm transition-none flex items-center justify-center gap-2 disabled:opacity-50">
              {buying ? <><CreditCard size={18} /> Processing Payment...</> : <><ShoppingCart size={18} /> Enroll Now - &#8377;{course.price}</>}
            </button>
            <div className="flex items-center justify-center gap-3 mt-2">
              <span className="text-[10px] text-slate-400">UPI</span>
              <span className="text-[10px] text-slate-300">|</span>
              <span className="text-[10px] text-slate-400">Cards</span>
              <span className="text-[10px] text-slate-300">|</span>
              <span className="text-[10px] text-slate-400">Net Banking</span>
              <span className="text-[10px] text-slate-300">|</span>
              <span className="text-[10px] text-slate-400">Wallets</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
